import { apiBuilder } from "@zodios/core";
import { z } from "zod";

// Uploaded Search Query Performance reports: `api/views/sqp_report.py` and the `searchtermreport` aggregator in
// `api/datamart/v2/searchtermreport.py` in app-rest-api.

const sqpReportSchema = z.object({
  id: z.number(),
  // Nested `UploadSerializer`; only the name is read.
  upload: z.object({ name: z.string() }).passthrough().nullable(),
  marketplace: z.string(),
  reporting_range: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  brand_or_asin: z.string(),
});

export type SQPReport = z.infer<typeof sqpReportSchema>;

const count = z.number().nullable();
const float = z.number().nullable();

// Amazon's `%` columns (`*_share`, `*_rate`) are stored as percentage points.
const sqpReportRowSchema = z.object({
  id: z.number(),
  report: z.number(),
  marketplace: z.string(),
  search_query: z.string(),
  reporting_date: z.string(),
  search_query_score: float,
  search_query_volume: count,
  impressions_total: count,
  impressions_brand_or_asin: count,
  impressions_brand_or_asin_share: float,
  clicks_total: count,
  clicks_total_rate: float,
  clicks_brand_or_asin: count,
  clicks_brand_or_asin_share: float,
  clicks_price_median: float,
  clicks_brand_or_asin_price_median: float,
  clicks_same_day_shipping_speed: count,
  clicks_1d_shipping_speed: count,
  clicks_2d_shipping_speed: count,
  cart_adds_total: count,
  cart_adds_total_rate: float,
  cart_adds_brand_or_asin: count,
  cart_adds_brand_or_asin_share: float,
  cart_adds_price_median: float,
  cart_adds_brand_or_asin_price_median: float,
  cart_adds_same_day_shipping_speed: count,
  cart_adds_1d_shipping_speed: count,
  cart_adds_2d_shipping_speed: count,
  purchases_total: count,
  purchases_total_rate: float,
  purchases_brand_or_asin: count,
  purchases_brand_or_asin_share: float,
  purchases_price_median: float,
  purchases_brand_or_asin_price_median: float,
  purchases_same_day_shipping_speed: count,
  purchases_1d_shipping_speed: count,
  purchases_2d_shipping_speed: count,
});

export type SQPReportRow = z.infer<typeof sqpReportRowSchema>;

/** The search term report metrics the dashboard joins onto each query. */
export const SQPR_SEARCH_TERM_METRICS = [
  "ad_spend",
  "ad_sales",
  "acos",
  "roas",
  "clicks",
  "impressions",
  "adv_targets",
  "adv_keywords",
] as const;

const searchTermStatsRowSchema = z.object({
  query: z.string(),
  ad_spend: float,
  ad_sales: float,
  acos: float,
  roas: float,
  clicks: count,
  impressions: count,
  adv_targets: z.array(z.number().nullable()),
  adv_keywords: z.array(z.number().nullable()),
});

export type SearchTermStatsRow = z.infer<typeof searchTermStatsRowSchema>;

const paginated = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({
    count: z.number(),
    next: z.string().nullable(),
    previous: z.string().nullable(),
    results: z.array(schema),
  });

export const sqpReportsApiDefinitions = apiBuilder({
  alias: "getSQPReports",
  method: "get",
  path: "/api/sqpreport/",
  parameters: [{ name: "marketplace", type: "Query", schema: z.string() }],
  response: paginated(sqpReportSchema),
})
  .addEndpoint({
    alias: "deleteSQPReport",
    method: "delete",
    path: "/api/sqpreport/:id/",
    status: 204,
    response: z.unknown(),
  })
  .addEndpoint({
    alias: "getSQPReportRows",
    method: "get",
    // The backend pages at 5,000 rows; the dashboard reads the first page, as it always has.
    path: "/api/sqpreportrow/",
    parameters: [{ name: "report", type: "Query", schema: z.number() }],
    response: paginated(sqpReportRowSchema),
  })
  .addEndpoint({
    alias: "getSQPRSearchTermStats",
    method: "get",
    path: "/api/data_report_v2/searchtermreport/stats/",
    parameters: [
      { name: "dimensions", type: "Query", schema: z.literal("query") },
      { name: "metrics", type: "Query", schema: z.string() },
      { name: "filters", type: "Query", schema: z.string() },
    ],
    response: z.array(searchTermStatsRowSchema),
  })
  .build();
